/**
 * Componente para mostrar el detalle completo de un ticket
 * 
 * Referencia: sistema-tickets-despliegue.md Sección 8.1
 */

import React, { useState, useContext, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantNavigation } from '../../hooks/soporte/useTenantNavigation';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Grid,
  Divider,
  Chip,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  alpha
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useSoporteData } from '../../hooks/soporte/useSoporteData';
import { useTicketActions } from '../../hooks/soporte/useTicketActions';
import { SoporteContext } from '../../context/SoporteContext';
import { ErpContext } from '../../context/ErpContext';
import { validarMensaje } from '../../utils/soporte/soporteValidations';
import EstadoBadge from './shared/EstadoBadge';
import PrioridadChip from './shared/PrioridadChip';
import SLAIndicator from './shared/SLAIndicator';
import MensajeItem from './shared/MensajeItem';
import TimelineEvento from './shared/TimelineEvento';
import AdjuntoUpload from './shared/AdjuntoUpload';
import axios from 'axios';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { 
  ESTADOS_VISIBLES, 
  LABELS_ESTADOS_VISIBLES, 
  getEstadoInternoDesdeVisible, 
  getEstadoVisible,
  getLabelEstadoVisible
} from '../../utils/soporte/estadosMapping';

dayjs.extend(relativeTime);
dayjs.locale('es');

const DetalleTicketComponent = () => {
  const { id } = useParams();
  const navigate = useTenantNavigation();
  const { serverURL } = useContext(ErpContext);
  const { categorias, prioridades, config } = useContext(SoporteContext);
  
  // Estado para rastrear el origen de navegación
  const [origenNavegacion, setOrigenNavegacion] = useState(null);
  // Ref para evitar múltiples lecturas del sessionStorage
  const origenLeidoRef = useRef(false);
  
  // Obtener rol del usuario desde cookies
  const userRole = Cookies.get('rolUsuario') || '';
  const userId = parseInt(Cookies.get('idUsuario') || '0');
  const esAgente = userRole === 'Agente Soporte' || userRole === 'Soporte' || 
                   userRole === 'Administrador';
  const esAdmin = userRole === 'Administrador';
  
  // Cargar datos del ticket
  const { data, loading, error, reload } = useSoporteData(
    `/tickets/${id}`,
    { include: 'mensajes,eventos,adjuntos' }
  );

  console.log("data para mauri :", data);
  
  const ticket = data?.ticket || data;
  
  // Hook de acciones
  const { 
    tomarTicket, 
    reasignarTicket, 
    cambiarEstado, 
    cambiarPrioridad, 
    cambiarCategoria,
    loading: actionsLoading,
    error: actionsError
  } = useTicketActions();
  
  // Estado para errores de acciones
  const [errorAccion, setErrorAccion] = useState('');
  
  // Estado del formulario de respuesta
  const [mensajeContent, setMensajeContent] = useState('');
  const [adjuntos, setAdjuntos] = useState([]);
  const [isInternal, setIsInternal] = useState(false);
  const [enviandoMensaje, setEnviandoMensaje] = useState(false);
  const [errorMensaje, setErrorMensaje] = useState('');
  
  // Estado para plantillas de respuesta
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState('');
  const [cargandoPlantillas, setCargandoPlantillas] = useState(false);
  
  // Estado para acciones de agente
  const [mostrarCambiarEstado, setMostrarCambiarEstado] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [mostrarCambiarPrioridad, setMostrarCambiarPrioridad] = useState(false);
  const [nuevaPrioridad, setNuevaPrioridad] = useState('');
  const [mostrarCambiarCategoria, setMostrarCambiarCategoria] = useState(false);
  const [nuevaCategoria, setNuevaCategoria] = useState('');
  
  // Estado para asignar/reasignar ticket (solo admin)
  const [mostrarAsignarAgente, setMostrarAsignarAgente] = useState(false);
  const [agenteSeleccionado, setAgenteSeleccionado] = useState('');
  const [agentes, setAgentes] = useState([]);
  const [cargandoAgentes, setCargandoAgentes] = useState(false);

  // Estado para eliminar ticket (soft delete) - solo admin
  const [mostrarConfirmarEliminar, setMostrarConfirmarEliminar] = useState(false);
  const [eliminandoTicket, setEliminandoTicket] = useState(false);
  
  // Estado para editar descripción del ticket
  const [editandoDescripcion, setEditandoDescripcion] = useState(false);
  const [nuevaDescripcion, setNuevaDescripcion] = useState('');
  const [guardandoDescripcion, setGuardandoDescripcion] = useState(false);
  
  // Estado para rastrear si necesitamos recargar después de una acción
  const [necesitaRecargar, setNecesitaRecargar] = useState(false);
  const prevActionsLoadingRef = useRef(actionsLoading);
  
  // Recargar datos solo cuando una acción se complete (de loading a no loading)
  useEffect(() => {
    // Solo recargar si actionsLoading cambió de true a false Y necesitamos recargar
    if (prevActionsLoadingRef.current === true && actionsLoading === false && necesitaRecargar) {
      reload();
      setNecesitaRecargar(false);
    }
    prevActionsLoadingRef.current = actionsLoading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionsLoading, necesitaRecargar]);
  
  // Obtener el origen de navegación al cargar el componente
  useEffect(() => {
    // Evitar múltiples lecturas del sessionStorage
    if (origenLeidoRef.current) {
      console.log('🔍 [DetalleTicket] Origen ya leído, omitiendo...');
      return;
    }
    
    const origen = sessionStorage.getItem('ticket_navegacion_origen');
    console.log('🔍 [DetalleTicket] Origen de navegación leído:', origen);
    
    if (origen === 'bandejas' || origen === 'tickets' || origen === 'crear') {
      setOrigenNavegacion(origen);
      origenLeidoRef.current = true;
      console.log('✅ [DetalleTicket] Origen establecido:', origen);
    } else {
      // Si no hay origen guardado, usar 'tickets' como predeterminado
      setOrigenNavegacion('tickets');
      origenLeidoRef.current = true;
      console.log('⚠️ [DetalleTicket] Origen no encontrado, usando predeterminado: tickets');
    }
    
    // Cleanup: limpiar sessionStorage cuando el componente se desmonte
    return () => {
      console.log('🧹 [DetalleTicket] Cleanup: limpiando sessionStorage');
      sessionStorage.removeItem('ticket_navegacion_origen');
    };
  }, []);
  
  // Cargar plantillas cuando el ticket esté disponible y el usuario sea agente
  useEffect(() => {
    if (ticket && esAgente) {
      cargarPlantillas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.categoriaId, esAgente]);
  
  /**
   * Cargar plantillas disponibles para la categoría del ticket
   */
  const cargarPlantillas = async () => {
    if (!ticket) return;
    
    setCargandoPlantillas(true);
    try {
      const categoriaId = ticket.categoriaId || ticket.categoria?.id;
      const url = categoriaId 
        ? `${serverURL}/soporteAPI/plantillas?categoriaId=${categoriaId}`
        : `${serverURL}/soporteAPI/plantillas`;
      
      const response = await axios.get(url, { withCredentials: true });
      const plantillasData = response.data.plantillas || response.data || [];
      
      // Filtrar solo plantillas públicas o activas
      const plantillasFiltradas = plantillasData.filter(p => 
        p.activa && !p.eliminado && (p.esPublica || true) // Por ahora mostrar todas las activas
      );
      
      setPlantillas(plantillasFiltradas);
    } catch (err) {
      console.error('Error cargando plantillas:', err);
      // No mostrar error al usuario, simplemente no cargar plantillas
      setPlantillas([]);
    } finally {
      setCargandoPlantillas(false);
    }
  };
  
  /**
   * Manejar selección de plantilla
   */
  const handleSeleccionarPlantilla = (event) => {
    const plantillaId = event.target.value;
    setPlantillaSeleccionada(plantillaId);
    
    if (plantillaId) {
      const plantilla = plantillas.find(p => p.id === plantillaId);
      if (plantilla) {
        // Insertar contenido de la plantilla en el campo de mensaje
        // Si ya hay contenido, agregarlo después
        if (mensajeContent.trim()) {
          setMensajeContent(mensajeContent + '\n\n' + plantilla.contenido);
        } else {
          setMensajeContent(plantilla.contenido);
        }
      }
    }
  };
  
  /**
   * Manejar envío de mensaje
   */
  const handleEnviarMensaje = async () => {
    // Validar mensaje
    const errorValidacion = validarMensaje(mensajeContent, adjuntos);
    if (errorValidacion) {
      setErrorMensaje(errorValidacion);
      return;
    }
    
    // Validar que solo agentes pueden enviar notas internas
    if (isInternal && !esAgente) {
      setErrorMensaje('Solo agentes pueden enviar notas internas');
      return;
    }
    
    setEnviandoMensaje(true);
    setErrorMensaje('');
    
    try {
      // Preparar datos para enviar como JSON con archivos en base64
      const datosParaEnviar = {
        content: mensajeContent.trim(),
        isInternal: isInternal,
        adjuntos: adjuntos.map(adjunto => ({
          name: adjunto.name,
          type: adjunto.type,
          size: adjunto.size,
          data: adjunto.data // Ya está en base64 desde AdjuntoUpload.js
        }))
      };
      
      // Determinar si cambiar estado al enviar
      if (ticket?.status === 'esperando_cliente') {
        // Si el cliente responde, cambiar a en_progreso
        datosParaEnviar.cambiarEstado = 'en_progreso';
      } else if (ticket?.status === 'resuelto' && !esAgente && !esAdmin) {
        // Si el ticket está resuelto y el cliente envía un mensaje, reabrir a en_progreso
        datosParaEnviar.cambiarEstado = 'en_progreso';
      }
      
      await axios.post(
        `${serverURL}/soporteAPI/tickets/${id}/messages`,
        datosParaEnviar,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Limpiar formulario
      setMensajeContent('');
      setAdjuntos([]);
      setIsInternal(false);
      setErrorMensaje('');
      
      // Recargar datos
      reload();
    } catch (err) {
      console.error('Error enviando mensaje:', err);
      setErrorMensaje(err.response?.data?.error || 'Error al enviar el mensaje');
    } finally {
      setEnviandoMensaje(false);
    }
  };
  
  /**
   * Manejar confirmar resolución (cliente)
   */
  const handleConfirmarResolucion = async () => {
    try {
      await cambiarEstado(id, 'resuelto');
      setNecesitaRecargar(true);
    } catch (err) {
      console.error('Error confirmando resolución:', err);
    }
  };
  
  /**
   * Manejar rechazar resolución (cliente)
   */
  const handleRechazarResolucion = async () => {
    try {
      await cambiarEstado(id, 'en_progreso');
      setNecesitaRecargar(true);
    } catch (err) {
      console.error('Error rechazando resolución:', err);
    }
  };
  
  /**
   * Manejar reabrir ticket (cliente)
   */
  const handleReabrirTicket = async () => {
    try {
      await cambiarEstado(id, 'en_progreso');
      setNecesitaRecargar(true);
    } catch (err) {
      console.error('Error reabriendo ticket:', err);
      setErrorAccion('Error al reabrir el ticket');
    }
  };
  
  /**
   * Manejar tomar ticket (agente)
   */
  const handleTomarTicket = async () => {
    const exito = await tomarTicket(id);
    if (exito) {
      setNecesitaRecargar(true);
    }
  };
  
  /**
   * Manejar cambiar estado (agente)
   */
  const handleAplicarCambioEstado = async () => {
    if (!nuevoEstado) return;
    
    setErrorAccion(''); // Limpiar error previo
    
    // Mapear el estado visible seleccionado al estado interno
    const estadoInterno = getEstadoInternoDesdeVisible(nuevoEstado, ticket?.status);
    
    const exito = await cambiarEstado(id, estadoInterno);
    if (exito) {
      setMostrarCambiarEstado(false);
      setNuevoEstado('');
      setNecesitaRecargar(true);
      setErrorAccion(''); // Limpiar error si fue exitoso
    } else {
      // Mostrar error del hook o mensaje genérico
      const mensajeError = actionsError || 'No puedes cambiar el estado del ticket';
      setErrorAccion(mensajeError);
    }
  };
  
  /**
   * Manejar cambiar prioridad (agente)
   */
  const handleAplicarCambioPrioridad = async () => {
    if (!nuevaPrioridad) return;
    
    const exito = await cambiarPrioridad(id, nuevaPrioridad);
    if (exito) {
      setMostrarCambiarPrioridad(false);
      setNuevaPrioridad('');
      setNecesitaRecargar(true);
    }
  };
  
  /**
   * Manejar cambiar categoría (agente)
   */
  const handleAplicarCambioCategoria = async () => {
    if (!nuevaCategoria) return;
    
    const exito = await cambiarCategoria(id, nuevaCategoria);
    if (exito) {
      setMostrarCambiarCategoria(false);
      setNuevaCategoria('');
      setNecesitaRecargar(true);
    }
  };
  
  /**
   * Cargar lista de agentes (solo admin)
   */
  const cargarAgentes = async () => {
    if (!esAdmin) return;
    
    setCargandoAgentes(true);
    try {
      // Obtener todos los usuarios
      const usuariosRes = await axios.get(`${serverURL}/adminAPI/getUsuario`, {
        withCredentials: true
      });
      
      // Obtener roles
      const rolesRes = await axios.get(`${serverURL}/adminAPI/getRolUsuario`, {
        withCredentials: true
      });
      
      const usuarios = usuariosRes.data || [];
      const roles = rolesRes.data || [];
      
      // Filtrar solo agentes y administradores
      const agentesData = usuarios
        .map(usuario => {
          const rolObj = roles.find(r => r.id === Number(usuario.rol) || r.id === usuario.rol);
          const rolNombre = rolObj ? rolObj.descripcion : '';
          const esAgente = rolNombre === 'Agente Soporte' || rolNombre === 'Soporte' || rolNombre === 'Administrador';
          if (esAgente) {
            return {
              id: usuario.id,
              nombreUsuario: usuario.usuario || usuario.nombreUsuario || 'N/A', // 'usuario' es el campo correcto
              usuario: usuario.usuario, // Mantener también el campo original
              nombre: usuario.nombre || '',
              apellido: usuario.apellido || '',
              nombreCompleto: `${usuario.nombre || ''} ${usuario.apellido || ''}`.trim() || usuario.usuario || 'N/A'
            };
          }
          return null;
        })
        .filter(Boolean);
      console.log("🔍 [DetalleTicket] Agentes cargados:", agentesData);
      setAgentes(agentesData);
      
      // Si el ticket ya tiene un agente asignado, seleccionarlo por defecto
      if (ticket?.agentId) {
        setAgenteSeleccionado(String(ticket.agentId));
      }
    } catch (err) {
      console.error('Error cargando agentes:', err);
      setErrorAccion('Error al cargar la lista de agentes');
    } finally {
      setCargandoAgentes(false);
    }
  };
  
  /**
   * Manejar abrir dialog de asignar agente
   */
  const handleAbrirAsignarAgente = () => {
    setMostrarAsignarAgente(true);
    setErrorAccion('');
    cargarAgentes();
  };
  
  /**
   * Manejar asignar/reasignar ticket a agente (solo admin)
   */
  const handleAsignarAgente = async () => {
    if (!agenteSeleccionado) {
      setErrorAccion('Debes seleccionar un agente');
      return;
    }
    
    setErrorAccion('');
    const exito = await reasignarTicket(id, parseInt(agenteSeleccionado));
    
    if (exito) {
      setMostrarAsignarAgente(false);
      setAgenteSeleccionado('');
      setNecesitaRecargar(true);
      setErrorAccion('');
    } else {
      const mensajeError = actionsError || 'Error al asignar el ticket al agente';
      setErrorAccion(mensajeError);
    }
  };

  /**
   * Manejar eliminar ticket (soft delete) - solo admin
   */
  const handleEliminarTicket = async () => {
    if (!id) return;
    
    setEliminandoTicket(true);
    setErrorAccion('');
    
    try {
      const response = await axios.delete(
        `${serverURL}/soporteAPI/tickets/${id}`,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200 || response.status === 204) {
        // Cerrar modal y navegar a la lista de tickets
        setMostrarConfirmarEliminar(false);
        setEliminandoTicket(false);
        
        // Mostrar mensaje de éxito
        alert(`Ticket ${ticket.numero} eliminado correctamente.`);
        
        // Navegar de vuelta a la lista según el origen
        handleNavegarAtras();
      } else {
        throw new Error('Error al eliminar el ticket');
      }
    } catch (err) {
      console.error('Error eliminando ticket:', err);
      setErrorAccion(err.response?.data?.error || 'Error al eliminar el ticket');
      setEliminandoTicket(false);
    }
  };

  /**
   * Manejar guardar descripción del ticket
   */
  const handleGuardarDescripcion = async () => {
    if (!nuevaDescripcion.trim() || !id) return;
    
    setGuardandoDescripcion(true);
    setErrorAccion('');
    
    try {
      console.log(`🔍 [handleGuardarDescripcion] Enviando PATCH a: ${serverURL}/soporteAPI/tickets/${id}/description`);
      console.log(`🔍 [handleGuardarDescripcion] Datos:`, { descripcion: nuevaDescripcion.trim() });
      
      const response = await axios.patch(
        `${serverURL}/soporteAPI/tickets/${id}/description`,
        { descripcion: nuevaDescripcion.trim() },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log(`✅ [handleGuardarDescripcion] Respuesta:`, response.data);
      
      if (response.status === 200) {
        setEditandoDescripcion(false);
        setNuevaDescripcion('');
        reload(); // Recargar datos del ticket
      } else {
        throw new Error('Error al guardar la descripción');
      }
    } catch (err) {
      console.error('❌ [handleGuardarDescripcion] Error:', err);
      console.error('❌ [handleGuardarDescripcion] Response:', err.response);
      setErrorAccion(err.response?.data?.error || 'Error al guardar la descripción');
    } finally {
      setGuardandoDescripcion(false);
    }
  };

  /**
   * Función para navegar hacia atrás según el origen
   */
  const handleNavegarAtras = () => {
    console.log('🔙 [DetalleTicket] Navegando hacia atrás desde origen:', origenNavegacion);
    
    // Limpiar sessionStorage antes de navegar
    sessionStorage.removeItem('ticket_navegacion_origen');
    
    // Caso 1: Vino desde Mis Tickets (bandejas)
    if (origenNavegacion === 'bandejas') {
      console.log('   ↪️ Navegando a: /soporte/bandejas (Mis Tickets)');
      navigate('/soporte/bandejas');
    }
    // Caso 2: Vino desde Tabla TICKETS o creó el ticket
    else if (origenNavegacion === 'tickets' || origenNavegacion === 'crear') {
      console.log('   ↪️ Navegando a: /soporte/tickets (Tabla TICKETS)');
      navigate('/soporte/tickets');
    }
    // Caso 3: Origen desconocido o no definido - usar tickets como predeterminado
    else {
      console.log('   ↪️ Navegando a: /soporte/tickets (Predeterminado)');
      navigate('/soporte/tickets');
    }
  };
  
  /**
   * Descargar adjunto usando fetch + blob
   * Esto fuerza la descarga del archivo en lugar de abrirlo en nueva pestaña
   */
  const handleDescargarAdjunto = async (adjunto) => {
    const tenant = Cookies.get('tenant') || 'erp';
    const filename = adjunto.name;
    const url = `${serverURL}/adjuntos-soporte/${tenant}/${id}/${filename}`;
    
    try {
      const response = await fetch(url, {
        credentials: 'include' // Importante para enviar cookies de sesión
      });
      
      if (!response.ok) {
        throw new Error(`Error al descargar: ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Crear enlace temporal y forzar descarga
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar memoria
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error al descargar adjunto:', error);
      // Fallback: abrir en nueva pestaña si falla fetch
      window.open(url, '_blank');
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || !ticket) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error || 'Ticket no encontrado'}
        </Alert>
        <Button onClick={handleNavegarAtras} sx={{ mt: 2 }}>
          Volver
        </Button>
      </Box>
    );
  }
  
  // Ordenar mensajes por fecha
  const mensajes = (ticket.mensajes || ticket.Mensajes || []).sort((a, b) => {
    return new Date(a.createdAt || a.createdAt) - new Date(b.createdAt || b.createdAt);
  });
  
  // Filtrar el primer mensaje que coincide con la descripción del ticket
  const descripcionTicket = (ticket.descripcion || '').trim().replace(/\s+/g, ' ');
  
  // Obtener adjuntos del ticket (para mostrar en la card de Descripción)
  // IMPORTANTE: Los adjuntos creados al crear el ticket están directamente en ticket.adjuntos (mensajeId: null)
  // Los adjuntos de mensajes posteriores NO deben mostrarse en la descripción del ticket
  const adjuntosDelTicket = ticket?.adjuntos || ticket?.Adjuntos || [];
  
  // Verificar si el primer mensaje es realmente el mensaje inicial (coincide con la descripción)
  const primerMensaje = mensajes.length > 0 ? mensajes[0] : null;
  const contenidoPrimerMensaje = (primerMensaje?.content || primerMensaje?.contenido || '').trim().replace(/\s+/g, ' ');
  const esMensajeInicial = primerMensaje && contenidoPrimerMensaje === descripcionTicket;
  
  // Solo incluir adjuntos del primer mensaje si realmente es el mensaje inicial (coincide con descripción)
  const adjuntosDelMensajeInicial = esMensajeInicial 
    ? (primerMensaje?.adjuntos || primerMensaje?.Attachments || [])
    : [];
  
  // Combinar adjuntos del ticket y del mensaje inicial (solo si es el verdadero mensaje inicial)
  const adjuntosIniciales = [...adjuntosDelTicket, ...adjuntosDelMensajeInicial];
  
  // Debug: Verificar estructura de adjuntos
  if (adjuntosIniciales.length > 0 || primerMensaje) {
    console.log('🔍 [DetalleTicket] Adjuntos del ticket:', adjuntosDelTicket);
    console.log('🔍 [DetalleTicket] Primer mensaje:', primerMensaje);
    console.log('🔍 [DetalleTicket] ¿Es mensaje inicial?:', esMensajeInicial);
    console.log('🔍 [DetalleTicket] Adjuntos del mensaje inicial:', adjuntosDelMensajeInicial);
    console.log('🔍 [DetalleTicket] Adjuntos iniciales combinados:', adjuntosIniciales);
  }
  const mensajesFiltrados = mensajes.filter((mensaje, index) => {
    // Si es el primer mensaje y su contenido coincide con la descripción, excluirlo
    if (index === 0) {
      const contenidoMensaje = (mensaje.content || mensaje.contenido || '').trim().replace(/\s+/g, ' ');
      // Comparar normalizando espacios múltiples
      return contenidoMensaje !== descripcionTicket;
    }
    return true;
  });
  
  // Ordenar eventos por fecha (más nuevo primero para AUDITORIA)
  const eventos = (ticket.eventos || ticket.Eventos || []).sort((a, b) => {
    const fechaA = new Date(a.createdAt || a.createdAt);
    const fechaB = new Date(b.createdAt || b.createdAt);
    return fechaB - fechaA; // Orden descendente: más nuevo primero
  });
  
  // CHAT solo contiene mensajes (sin eventos) - orden descendente: más nuevo primero
  const timelineItems = mensajesFiltrados.map(m => ({ ...m, tipo: 'mensaje' })).sort((a, b) => {
    const fechaA = new Date(a.createdAt || a.createdAt);
    const fechaB = new Date(b.createdAt || b.createdAt);
    return fechaB - fechaA; // Orden descendente: más nuevo primero
  });
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header con título centrado */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Tooltip title={origenNavegacion === 'bandejas' ? 'Volver a Mis Tickets' : 'Volver a Tickets'}>
          <IconButton onClick={handleNavegarAtras}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="h4" sx={{ textAlign: 'center' }}>
            {ticket.numero} - {ticket.titulo}
          </Typography>
        </Box>
        {/* Espacio invisible para balancear el layout */}
        <Box sx={{ width: 48 }} />
      </Stack>
      
      {/* Layout vertical descendente */}
      <Stack spacing={3}>
         {/* 1. Card de Información del Ticket - Ancho completo */}
         <Paper elevation={2} sx={{ p: 2 }}>
           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
             <Typography variant="h6">
               Información del Ticket
             </Typography>
             
             {/* Botón para eliminar ticket (solo administradores) */}
             {esAdmin && (
               <Tooltip title="Eliminar ticket">
                 <IconButton
                   onClick={() => setMostrarConfirmarEliminar(true)}
                   size="small"
                   sx={{
                     color: 'white',
                     backgroundColor: (theme) => theme.palette.error.main,
                     borderRadius: 1.5,
                     p: 0.75,
                     '&:hover': {
                       backgroundColor: (theme) => alpha(theme.palette.error.main, 0.8),
                       transform: 'scale(1.05)',
                     },
                     transition: 'all 0.2s ease',
                   }}
                 >
                   <DeleteIcon fontSize="small" />
                 </IconButton>
               </Tooltip>
             )}
           </Box>
          <Divider sx={{ my: 2 }} />
          
          {/* Grid responsive: una línea en pantallas grandes, se adapta progresivamente */}
          <Grid container spacing={3} sx={{ width: '75%', margin: '0 auto', justifyContent: 'space-between' }}>
            {/* Creado */}
            <Grid item xs={12} sm={6} md={4} lg="auto">
              <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.144rem' }}>
                  Creado
                </Typography>
                <Typography variant="body2" mt={0.5} sx={{ fontSize: '1.4rem', display: 'flex', alignItems: 'center', minHeight: '40px', justifyContent: 'center' }}>
                  {ticket.createdAt ? dayjs(ticket.createdAt).format('DD/MM/YYYY HH:mm') : 'N/A'}
                </Typography>
              </Box>
            </Grid>
            
            {/* Creado Por */}
            <Grid item xs={12} sm={6} md={4} lg="auto">
              <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.144rem' }}>
                  Creado Por
                </Typography>
                <Typography variant="body2" mt={0.5} sx={{ fontSize: '1.4rem', display: 'flex', alignItems: 'center', minHeight: '40px', justifyContent: 'center' }}>
                  {ticket.requester?.usuario || 
                   (ticket.requester?.nombre && ticket.requester?.apellido 
                     ? `${ticket.requester.nombre} ${ticket.requester.apellido}`.trim()
                     : ticket.requester?.nombre) ||
                   'N/A'}
                </Typography>
              </Box>
            </Grid>
            
            {/* Agente Asignado */}
            <Grid item xs={12} sm={6} md={4} lg="auto">
              <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.144rem' }}>
                  Agente Asignado
                </Typography>
                <Typography variant="body2" mt={0.5} sx={{ fontSize: '1.4rem', display: 'flex', alignItems: 'center', minHeight: '40px', justifyContent: 'center' }}>
                  {ticket.agentId 
                    ? (ticket.Agent?.nombreUsuario || 
                       ticket.Agent?.nombre || 
                       ticket.agent?.nombreUsuario || 
                       'N/A')
                    : 'Sin asignar'}
                </Typography>
              </Box>
            </Grid>
            
            {/* Resuelto (condicional) */}
            {(ticket.resolvedAt || ticket.resolvedAt) && (
              <Grid item xs={12} sm={6} md={4} lg="auto">
                <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.144rem' }}>
                    Resuelto
                  </Typography>
                  <Typography variant="body2" mt={0.5} sx={{ fontSize: '1.4rem', display: 'flex', alignItems: 'center', minHeight: '40px', justifyContent: 'center' }}>
                    {dayjs(ticket.resolvedAt || ticket.resolvedAt).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {/* Estado */}
            <Grid item xs={12} sm={6} md={4} lg="auto">
              <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.144rem' }}>
                  Estado
                </Typography>
                <Box mt={0.5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40px', '& .MuiChip-root': { fontSize: '1.4rem', height: 'auto', padding: '4px 12px', display: 'flex', alignItems: 'center' } }}>
                  <EstadoBadge status={ticket.status} />
                </Box>
              </Box>
            </Grid>
            
            {/* Prioridad */}
            <Grid item xs={12} sm={6} md={4} lg="auto">
              <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.144rem' }}>
                  Prioridad
                </Typography>
                <Box mt={0.5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40px', '& .MuiChip-root, & .MuiBox-root': { fontSize: '1.4rem', display: 'flex', alignItems: 'center' } }}>
                  <PrioridadChip prioridad={ticket.Prioridad || ticket.prioridad} />
                </Box>
                {esAgente && (
                  <Button
                    size="small"
                    onClick={() => setMostrarCambiarPrioridad(true)}
                    sx={{ mt: 1 }}
                  >
                    Cambiar
                  </Button>
                )}
              </Box>
            </Grid>
            
            {/* Categoría */}
            <Grid item xs={12} sm={6} md={4} lg="auto">
              <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.144rem' }}>
                  Categoría
                </Typography>
                <Typography variant="body2" mt={0.5} sx={{ fontSize: '1.4rem', display: 'flex', alignItems: 'center', minHeight: '40px', justifyContent: 'center' }}>
                  {ticket.Categoria?.nombre || ticket.categoria?.nombre || 'N/A'}
                </Typography>
                {esAgente && (
                  <Button
                    size="small"
                    onClick={() => setMostrarCambiarCategoria(true)}
                    sx={{ mt: 1 }}
                  >
                    Cambiar
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
          
          {/* Acciones (botones) - Fuera del Grid */}
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              {/* Botón Tomar Ticket (si no tiene agente asignado y es agente o admin) */}
              {!ticket.agentId && (esAgente || esAdmin) && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleTomarTicket}
                  disabled={actionsLoading}
                  sx={{ px: 2 }}
                >
                  Tomar Ticket
                </Button>
              )}
              
              {/* Botón Cambiar Estado (agentes y admins) */}
              {(esAgente || esAdmin) && (
                <Button
                  variant="outlined"
                  onClick={() => setMostrarCambiarEstado(true)}
                  disabled={actionsLoading}
                  sx={{ px: 2 }}
                >
                  Cambiar Estado
                </Button>
              )}
              
              {/* Botón Reasignar Ticket (solo admins) */}
              {esAdmin && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleAbrirAsignarAgente}
                  disabled={actionsLoading}
                  sx={{ px: 2 }}
                >
                  {ticket.agentId ? 'Reasignar Ticket' : 'Asignar Ticket'}
                </Button>
              )}
            </Box>
            
            {/* Botones Confirmar/Rechazar (Cliente) - Pendiente Validación */}
            {ticket.status === 'pendiente_validacion' && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleConfirmarResolucion}
                    disabled={actionsLoading}
                    sx={{ px: 2 }}
                  >
                    Confirmar Resolución
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRechazarResolucion}
                    disabled={actionsLoading}
                    sx={{ px: 2 }}
                  >
                    Volver a abrir
                  </Button>
                </Box>
              </>
            )}
            
            {/* Botones Confirmar/Rechazar (Cliente) - Pendiente Validación Nube */}
            {ticket.status === 'pendiente_validacion_nube' && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleConfirmarResolucion}
                    disabled={actionsLoading}
                    sx={{ px: 2 }}
                  >
                    Confirmar Resolución
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRechazarResolucion}
                    disabled={actionsLoading}
                    sx={{ px: 2 }}
                  >
                    Volver a abrir
                  </Button>
                </Box>
              </>
            )}
            
            {/* Botón Reabrir Ticket (solo cuando está resuelto y fue creado por el usuario actual) */}
            {ticket.status === 'resuelto' && ticket.requesterId === userId && (
              <>
                <Divider sx={{ mb: 2, mt: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleReabrirTicket}
                    disabled={actionsLoading}
                    sx={{ px: 3 }}
                  >
                    {actionsLoading ? 'Reabriendo...' : 'Reabrir Ticket'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Paper>
        
        {/* 2. Descripción */}
        <Paper elevation={2} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: adjuntosIniciales.length > 0 ? 2 : 0 }}>
            <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
              DESCRIPCIÓN :
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              {editandoDescripcion ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={nuevaDescripcion}
                  onChange={(e) => setNuevaDescripcion(e.target.value)}
                  sx={{ fontSize: '1.196rem' }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', fontSize: '1.196rem', flex: 1 }}>
                  {ticket.descripcion || 'SIN DESCRIPCIÓN'}
                </Typography>
              )}
              {!editandoDescripcion && ticket.requesterId === userId && (
                <Tooltip title="Editar descripción">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditandoDescripcion(true);
                      setNuevaDescripcion(ticket.descripcion || '');
                    }}
                    sx={{ mt: -0.5 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          {editandoDescripcion && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setEditandoDescripcion(false);
                  setNuevaDescripcion('');
                }}
              >
                Cancelar
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleGuardarDescripcion}
                disabled={guardandoDescripcion || !nuevaDescripcion.trim()}
              >
                {guardandoDescripcion ? 'Guardando...' : 'Guardar'}
              </Button>
            </Box>
          )}
          
          {/* Adjuntos del mensaje inicial (Caso 1) */}
          {adjuntosIniciales.length > 0 && (
            <Box mt={2} sx={{ display: 'inline-block', width: 'fit-content' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block" sx={{ mb: 1 }}>
                Archivos adjuntos:
              </Typography>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 1.5, 
                bgcolor: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}>
                <List dense sx={{ py: 0 }}>
                  {adjuntosIniciales.map((adj, index) => {
                    const adjId = adj.id || adj.uuid || index;
                    const adjName = adj.name || adj.nombre || 'Archivo sin nombre';
                    const adjSize = adj.size || 0;
                    
                    return (
                      <ListItem
                        key={adjId}
                        dense
                        sx={{
                          bgcolor: 'rgba(0, 0, 0, 0.03)',
                          borderRadius: 1,
                          mb: 0.5,
                          px: 1.5,
                          pr: 6, // Espacio para el botón de descarga
                          '&:last-child': { mb: 0 }
                        }}
                        secondaryAction={
                          <Tooltip title="Descargar">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleDescargarAdjunto(adj)}
                              aria-label="descargar adjunto"
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: '36px' }}>
                          <AttachFileIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={adjName}
                          secondary={`${(parseInt(adjSize) / 1024).toFixed(2)} KB`}
                          sx={{ pr: 4 }} // Espacio adicional para evitar sobreposición
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Box>
          )}
        </Paper>
        
        {/* 3. Card para Responder */}
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            RESPUESTA
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {errorMensaje && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMensaje('')}>
              {errorMensaje}
            </Alert>
          )}
          
          <Stack spacing={2}>
            {/* Selector de Plantillas (solo para agentes) - OCULTO */}
            {esAgente && plantillas.length > 0 && (
              <FormControl fullWidth sx={{ display: 'none' }}>
                <InputLabel>Usar Plantilla de Respuesta</InputLabel>
                <Select
                  value={plantillaSeleccionada}
                  onChange={handleSeleccionarPlantilla}
                  label="Usar Plantilla de Respuesta"
                  disabled={cargandoPlantillas}
                >
                  <MenuItem value="">
                    <em>Seleccionar plantilla...</em>
                  </MenuItem>
                  {plantillas.map(plantilla => (
                    <MenuItem key={plantilla.id} value={plantilla.id}>
                      {plantilla.nombre}
                      {plantilla.categoria && ` (${plantilla.categoria.nombre})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Mensaje"
              value={mensajeContent}
              onChange={(e) => setMensajeContent(e.target.value)}
              placeholder="Escribe tu respuesta..."
            />
            
            {/* Checkbox Nota Interna (solo agentes) - OCULTO */}
            {esAgente && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isInternal}
                    onChange={(e) => setIsInternal(e.target.checked)}
                  />
                }
                label="Nota interna (solo visible para agentes)"
                sx={{ display: 'none' }}
              />
            )}
            
            {/* Adjuntos */}
            <AdjuntoUpload
              adjuntos={adjuntos}
              onChange={setAdjuntos}
              maxFiles={config?.maxAttachmentsPerMessage || 5}
              maxSizeMB={config?.maxAttachmentSizeMb || 50}
            />
            
            <Button
              variant="contained"
              onClick={handleEnviarMensaje}
              disabled={enviandoMensaje || !mensajeContent.trim()}
              fullWidth
            >
              {enviandoMensaje ? 'Enviando...' : 'Enviar Mensaje'}
            </Button>
          </Stack>
        </Paper>
        
        {/* 4. CHAT (solo mensajes) */}
        <Paper elevation={2} sx={{ p: 2, maxHeight: 700, overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            CHAT
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {timelineItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay mensajes
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {timelineItems.map((item, index) => {
                const fecha = item.createdAt || item.createdAt;
                const fechaFormateada = fecha ? dayjs(fecha).format('DD/MM/YYYY HH:mm') : '';
                const nombreUsuario = item.Usuario?.nombreUsuario || 
                                     item.usuario?.nombreUsuario || 
                                     item.Author?.nombreUsuario ||
                                     item.author?.nombreUsuario ||
                                     item.Usuario?.nombre || 
                                     item.usuario?.nombre || 
                                     item.Author?.nombre ||
                                     item.author?.nombre ||
                                     'Usuario';
                const contenido = item.content || item.contenido || '';
                
                // Obtener adjuntos del mensaje
                const adjuntos = item.adjuntos || item.Attachments || [];
                
                return (
                  <Box key={`mensaje-${item.id || index}`}>
                    {/* Línea principal: Fecha, Usuario, Mensaje */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: index % 2 === 0 ? 'rgba(91, 91, 95, 0.03)' : 'transparent',
                        borderLeft: '3px solid',
                        borderLeftColor: index % 2 === 0 ? '#5b5b5f' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(91, 91, 95, 0.08)',
                        },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '1.25rem', // ✅ Reducido otro 10% (de 1.764rem a 1.5876rem)
                          color: '#718096',
                          minWidth: '160px',
                          fontWeight: 500,
                          fontFamily: 'monospace'
                        }}
                      >
                        {fechaFormateada}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '1.25rem', // ✅ Reducido otro 10% (de 1.764rem a 1.5876rem)
                          color: '#1976d2',
                          minWidth: '140px',
                          fontWeight: 600
                        }}
                      >
                        {nombreUsuario}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '1.25rem', // ✅ Reducido otro 10% (de 1.764rem a 1.5876rem)
                          color: '#2d3748',
                          flex: 1,
                          whiteSpace: 'pre-line',
                          overflowWrap: 'anywhere'
                        }}
                      >
                        {contenido}
                      </Typography>
                    </Box>
                    
                    {/* Adjuntos debajo del mensaje */}
                    {adjuntos.length > 0 && (
                      <Box mt={1} ml={2} sx={{ display: 'inline-block', width: 'fit-content' }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom display="block" sx={{ fontSize: '0.9072rem', mb: 1 }}>
                          Archivos adjuntos:
                        </Typography>
                        <Box sx={{ 
                          p: 1.5, 
                          borderRadius: 1.5, 
                          bgcolor: 'rgba(0, 0, 0, 0.02)',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                        }}>
                          <List dense sx={{ py: 0 }}>
                            {adjuntos.map((adj, adjIndex) => {
                              const adjId = adj.id || adj.uuid || adjIndex;
                              const adjName = adj.name || adj.nombre || 'Archivo sin nombre';
                              const adjSize = adj.size || 0;
                              
                              return (
                                <ListItem
                                  key={adjId}
                                  dense
                                  sx={{
                                    bgcolor: 'rgba(0, 0, 0, 0.03)',
                                    borderRadius: 1,
                                    mb: 0.5,
                                    px: 1.5,
                                    pr: 6, // Espacio para el botón de descarga
                                    alignItems: 'center', // Centrar verticalmente todo el contenido
                                    '&:last-child': { mb: 0 }
                                  }}
                                  secondaryAction={
                                    <Tooltip title="Descargar">
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={() => handleDescargarAdjunto(adj)}
                                        aria-label="descargar adjunto"
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}
                                      >
                                        <DownloadIcon />
                                      </IconButton>
                                    </Tooltip>
                                  }
                                >
                                  <ListItemIcon sx={{ minWidth: '36px', display: 'flex', alignItems: 'center' }}>
                                    <AttachFileIcon fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={adjName}
                                    secondary={`${(parseInt(adjSize) / 1024).toFixed(2)} KB`}
                                    primaryTypographyProps={{ sx: { fontSize: '0.9072rem' } }}
                                    secondaryTypographyProps={{ sx: { fontSize: '0.7938rem' } }}
                                    sx={{ 
                                      pr: 4, // Espacio adicional para evitar sobreposición
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      my: 0
                                    }}
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Paper>
        
        {/* 5. AUDITORIA (solo para agentes y administradores) */}
        {esAgente && (
          <Paper elevation={2} sx={{ p: 2, maxHeight: 700, overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              AUDITORIA
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {eventos.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay eventos registrados
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {eventos.map((evento, index) => {
                  // Función para traducir estados al español usando el mapeo de estados visibles
                  const traducirEstado = (estado) => {
                    if (!estado) return '';
                    // Obtener el estado visible y su label
                    const estadoVisible = getEstadoVisible(estado);
                    return getLabelEstadoVisible(estadoVisible);
                  };
                  
                  // Función para obtener descripción del evento
                  const getDescripcionEvento = () => {
                    const eventType = evento.eventType || evento.eventType || '';
                    // Parsear payload si viene como string JSON
                    let payload = evento.payload || {};
                    if (typeof payload === 'string') {
                      try {
                        payload = JSON.parse(payload);
                      } catch (e) {
                        console.warn('Error parsing payload:', e);
                        payload = {};
                      }
                    }
                    
                    // Si hay descripción en el payload, usarla
                    if (payload.descripcion) {
                      return payload.descripcion;
                    }
                    
                    // Manejar cambios de estado con formato específico
                    if (eventType === 'status_changed' || eventType === 'status_changed') {
                      const estadoAnterior = payload.oldStatus || payload.old_status || payload.fromStatus || '';
                      const estadoNuevo = payload.newStatus || payload.new_status || payload.toStatus || '';
                      
                      if (estadoAnterior && estadoNuevo) {
                        const estadoAnteriorTraducido = traducirEstado(estadoAnterior);
                        const estadoNuevoTraducido = traducirEstado(estadoNuevo);
                        return `Cambio de estado: ${estadoAnteriorTraducido} a ${estadoNuevoTraducido}`;
                      }
                      return 'Cambio de estado';
                    }
                    
                    // Traducir otros tipos de eventos al español
                    switch (eventType) {
                      case 'created':
                        // Obtener adjuntosCount del payload del evento (guardado al crear el ticket)
                        const adjuntosCountCreated = payload.adjuntosCount || 
                                                     payload.adjuntos_count || 
                                                     0;
                        // Si no está en el payload, usar los adjuntos directos del ticket (mensajeId: null)
                        // NO usar ticket.mensajes[0].adjuntos porque puede ser un mensaje agregado después
                        if (adjuntosCountCreated === 0 && ticket?.adjuntos) {
                          const adjuntosCountFromTicket = (ticket.adjuntos || []).length;
                          if (adjuntosCountFromTicket > 0) {
                            const archivoTextoCreated = adjuntosCountFromTicket === 1 ? 'archivo adjunto' : 'archivos adjuntos';
                            return `Ticket creado, ${adjuntosCountFromTicket} ${archivoTextoCreated}`;
                          }
                        }
                        if (adjuntosCountCreated > 0) {
                          const archivoTextoCreated = adjuntosCountCreated === 1 ? 'archivo adjunto' : 'archivos adjuntos';
                          return `Ticket creado, ${adjuntosCountCreated} ${archivoTextoCreated}`;
                        }
                        return 'Ticket creado';
                      case 'assigned':
                      case 'reassigned':
                        return payload.agentName || payload.agent_name
                          ? `Ticket asignado a ${payload.agentName || payload.agent_name}`
                          : 'Ticket asignado';
                      case 'reopened':
                        return payload.reopenCount || payload.reopenCount
                          ? `Ticket reabierto (${payload.reopenCount || payload.reopenCount} vez)`
                          : 'Ticket reabierto';
                      case 'message_added':
                        const adjuntosCount = payload.adjuntosCount || payload.adjuntos_count || 0;
                        if (adjuntosCount > 0) {
                          const archivoTexto = adjuntosCount === 1 ? 'archivo adjunto' : 'archivos adjuntos';
                          return `Mensaje agregado, ${adjuntosCount} ${archivoTexto}`;
                        }
                        return 'Mensaje agregado';
                      case 'priority_changed':
                        return payload.oldPriority && payload.newPriority
                          ? `Prioridad cambiada: ${payload.oldPriority} a ${payload.newPriority}`
                          : payload.old_priority && payload.new_priority
                          ? `Prioridad cambiada: ${payload.old_priority} a ${payload.new_priority}`
                          : 'Prioridad cambiada';
                      case 'category_changed':
                        return payload.oldCategory && payload.newCategory
                          ? `Categoría cambiada: ${payload.oldCategory} a ${payload.newCategory}`
                          : payload.old_category && payload.new_category
                          ? `Categoría cambiada: ${payload.old_category} a ${payload.new_category}`
                          : 'Categoría cambiada';
                      case 'resolved':
                        return 'Ticket resuelto';
                      case 'closed':
                        return payload.reason === 'autoclose_after_resolved'
                          ? 'Ticket cerrado automáticamente'
                          : 'Ticket cerrado';
                      default:
                        // Si el eventType está en inglés, intentar traducirlo
                        const traducciones = {
                          'reopened': 'Ticket reabierto',
                          'message_added': 'Mensaje agregado',
                          'priority_changed': 'Prioridad cambiada',
                          'category_changed': 'Categoría cambiada',
                          'status_changed': 'Cambio de estado',
                          'assigned': 'Ticket asignado',
                          'reassigned': 'Ticket reasignado',
                          'created': 'Ticket creado',
                          'resolved': 'Ticket resuelto',
                          'closed': 'Ticket cerrado'
                        };
                        return traducciones[eventType] || eventType || 'Evento';
                    }
                  };
                  
                  const descripcionEvento = getDescripcionEvento();
                  const fechaFormateada = evento.createdAt ? dayjs(evento.createdAt).format('DD/MM/YYYY HH:mm') : '';
                  
                  return (
                    <Box key={`evento-${evento.id || index}`}>
                      {/* Línea principal: Fecha, Descripción del evento */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: index % 2 === 0 ? 'rgba(91, 91, 95, 0.03)' : 'transparent',
                          borderLeft: '3px solid',
                          borderLeftColor: index % 2 === 0 ? '#5b5b5f' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(91, 91, 95, 0.08)',
                          },
                          transition: 'background-color 0.2s',
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '1.25rem',
                            color: '#718096',
                            minWidth: '160px',
                            fontWeight: 500,
                            fontFamily: 'monospace'
                          }}
                        >
                          {fechaFormateada}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '1.25rem',
                            color: '#2d3748',
                            flex: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {descripcionEvento}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        )}
      </Stack>
      
      {/* Dialog Cambiar Estado */}
      <Dialog open={mostrarCambiarEstado} onClose={() => {
        setMostrarCambiarEstado(false);
        setErrorAccion(''); // Limpiar error al cerrar
      }}>
        <DialogTitle>Cambiar Estado</DialogTitle>
        <DialogContent>
          {errorAccion && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorAccion('')}>
              {errorAccion}
            </Alert>
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Nuevo Estado</InputLabel>
            <Select
              value={nuevoEstado}
              onChange={(e) => {
                setNuevoEstado(e.target.value);
                setErrorAccion(''); // Limpiar error al cambiar selección
              }}
              label="Nuevo Estado"
            >
              <MenuItem value={ESTADOS_VISIBLES.ABIERTO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.ABIERTO]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.EN_PROGRESO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.EN_PROGRESO]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.COMPLETADO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.COMPLETADO]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.PRUEBA}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.PRUEBA]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.CERRADO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.CERRADO]}
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMostrarCambiarEstado(false);
            setErrorAccion(''); // Limpiar error al cancelar
          }}>Cancelar</Button>
          <Button 
            onClick={handleAplicarCambioEstado} 
            variant="contained" 
            disabled={!nuevoEstado || actionsLoading}
          >
            {actionsLoading ? 'Aplicando...' : 'Aplicar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog Cambiar Prioridad */}
      <Dialog open={mostrarCambiarPrioridad} onClose={() => setMostrarCambiarPrioridad(false)}>
        <DialogTitle>Cambiar Prioridad</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Nueva Prioridad</InputLabel>
            <Select
              value={nuevaPrioridad}
              onChange={(e) => setNuevaPrioridad(e.target.value)}
              label="Nueva Prioridad"
            >
              {prioridades
                .filter(p => p.activa && !p.eliminado)
                .sort((a, b) => (a.orden || 0) - (b.orden || 0))
                .map(prioridad => (
                  <MenuItem key={prioridad.id} value={prioridad.id}>
                    {prioridad.nombre}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMostrarCambiarPrioridad(false)}>Cancelar</Button>
          <Button onClick={handleAplicarCambioPrioridad} variant="contained" disabled={!nuevaPrioridad}>
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog Cambiar Categoría */}
      <Dialog open={mostrarCambiarCategoria} onClose={() => setMostrarCambiarCategoria(false)}>
        <DialogTitle>Cambiar Categoría</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Nueva Categoría</InputLabel>
            <Select
              value={nuevaCategoria}
              onChange={(e) => setNuevaCategoria(e.target.value)}
              label="Nueva Categoría"
            >
              {categorias
                .filter(c => c.activa && !c.eliminado)
                .sort((a, b) => (a.orden || 0) - (b.orden || 0))
                .map(categoria => (
                  <MenuItem key={categoria.id} value={categoria.id}>
                    {categoria.nombre}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMostrarCambiarCategoria(false)}>Cancelar</Button>
          <Button onClick={handleAplicarCambioCategoria} variant="contained" disabled={!nuevaCategoria}>
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog Asignar/Reasignar Agente (solo admin) */}
      <Dialog open={mostrarAsignarAgente} onClose={() => {
        setMostrarAsignarAgente(false);
        setAgenteSeleccionado('');
        setErrorAccion('');
      }}>
        <DialogTitle>{ticket?.agentId ? 'Reasignar Ticket' : 'Asignar Ticket'}</DialogTitle>
        <DialogContent>
          {errorAccion && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorAccion('')}>
              {errorAccion}
            </Alert>
          )}
          {cargandoAgentes ? (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Seleccionar Agente</InputLabel>
              <Select
                value={agenteSeleccionado}
                onChange={(e) => {
                  setAgenteSeleccionado(e.target.value);
                  setErrorAccion(''); // Limpiar error al cambiar selección
                }}
                label="Seleccionar Agente"
              >
                {agentes.map(agente => (
                  <MenuItem key={agente.id} value={String(agente.id)}>
                    {agente.nombreCompleto} ({agente.nombreUsuario})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {ticket?.agentId && !cargandoAgentes && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Agente actual: {ticket.Agent?.nombreUsuario || ticket.agent?.nombreUsuario || 'N/A'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMostrarAsignarAgente(false);
            setAgenteSeleccionado('');
            setErrorAccion('');
          }}>Cancelar</Button>
          <Button 
            onClick={handleAsignarAgente} 
            variant="contained" 
            disabled={!agenteSeleccionado || actionsLoading || cargandoAgentes}
          >
            {actionsLoading ? 'Asignando...' : (ticket?.agentId ? 'Reasignar' : 'Asignar')}
          </Button>
         </DialogActions>
       </Dialog>

       {/* Modal de confirmación para eliminar ticket */}
       <Dialog
         open={mostrarConfirmarEliminar}
         onClose={() => {
           if (!eliminandoTicket) {
             setMostrarConfirmarEliminar(false);
             setErrorAccion('');
           }
         }}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>Confirmar Eliminación</DialogTitle>
         <DialogContent>
           <Typography variant="body1" sx={{ mb: 2 }}>
             ¿Estás seguro de que deseas eliminar el ticket <strong>{ticket.numero}</strong>?
           </Typography>
           <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
             Esta acción realizará un <strong>soft delete</strong>, lo que significa que el ticket no aparecerá más en las listas por defecto, pero permanecerá en la base de datos.
           </Typography>
           
           {errorAccion && (
             <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorAccion('')}>
               {errorAccion}
             </Alert>
           )}
         </DialogContent>
         <DialogActions>
           <Button
             onClick={() => {
               setMostrarConfirmarEliminar(false);
               setErrorAccion('');
             }}
             disabled={eliminandoTicket}
           >
             Cancelar
           </Button>
           <Button
             onClick={handleEliminarTicket}
             variant="contained"
             color="error"
             disabled={eliminandoTicket}
             startIcon={<DeleteIcon />}
           >
             {eliminandoTicket ? 'Eliminando...' : 'Sí, eliminar ticket'}
           </Button>
         </DialogActions>
       </Dialog>
     </Box>
   );
 };

export default DetalleTicketComponent;
